import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import "./Dashboard.css";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { apiUrl } from "../config/apiBase";
import Footer from "../Landing Page/Footer";
import Feedback from "./Feedback";
import { useSubscription } from "../SubscriptionContext";

const FOCUS_AREAS = [
  // Mechanical Engineering Focus Areas
  "Thermodynamics & Heat Transfer",
  "Fluid Mechanics & Hydraulics",
  "Materials Selection & FEA",
  "Dynamics & Vibration Analysis",
  "Mechanical Design (CAD/CAM) & Prototyping",
  "Manufacturing Processes & Drafting (GD&T) ",
  "Project Management & Planning",
  "Research & Development",
  "Aerodynamics (CFD) & Propulsion",

  // Electrical Engineering Focus Areas
  "Circuit Analysis & Design",
  "Power Systems & Electronics",
  "Control Systems & Automation",

  // Civil Engineering Focus Areas
  "Structural Analysis & Design",
  
  // Industrial Engineering Focus Areas
  "Quality Control & Six Sigma",
  "Supply Chain Management",
];

function getDifficultyLabel(value) {
  if (value < 25) return "Entry";
  if (value < 75) return "Senior";
  return "Expert";
}

function Dashboard() {
  const navigate = useNavigate();
  const { refreshTokenIfNeeded } = useAuth();
  const { canGenerateQuestions, getUsageInfo } = useSubscription();
  const [jobUrl, setJobUrl] = useState("");
  const [focusArea, setFocusArea] = useState("");
  const [difficultyValue, setDifficultyValue] = useState(0);
  const [isTechnical, setIsTechnical] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [cvFile, setCvFile] = useState(null);
  const [usageLimitReached, setUsageLimitReached] = useState(false);
  const [lastJob, setLastJob] = useState(null);

  const loadingSteps = [
    { text: "Analyzing job post...", duration: 800 }, // Reduced from 1000ms
    { text: "Processing requirements...", duration: 1500 }, // Reduced from 2000ms
    { text: "Generating questions...", duration: 800 }, // Reduced from 1000ms
    { text: "Almost ready...", duration: 500 } // Reduced from 1000ms - total ~3.6s instead of 5s
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user can generate questions before proceeding
    if (!canGenerateQuestions()) {
      const usageInfo = getUsageInfo();
      alert(`You've reached your question limit (${usageInfo?.used}/${usageInfo?.limit}). Please upgrade to generate more questions.`);
      return;
    }

    setIsLoading(true);
    setCurrentStep(0);

    try {
      const token = await refreshTokenIfNeeded();
      if (!token) {
        setIsLoading(false);
        return;
      }
      // Start progress animation
      for (let i = 0; i < loadingSteps.length; i++) {
        setCurrentStep(i);
        await new Promise(resolve => setTimeout(resolve, loadingSteps[i].duration));
      }
      let resp, hrResp, sessionId;
  let usageLimit = false;
  let lastJobData = null;
      if (isTechnical) {
        try {
          resp = await axios.post(
            apiUrl("/api/jobs"),
            {
              job_url: jobUrl,
              stem_field: focusArea,
              difficulty: getDifficultyLabel(difficultyValue),
              interview_type: 'technical'
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              },
              withCredentials: true
            }
          );
        } catch (err) {
          if (err.response?.status === 403 && err.response?.data?.usageLimit) {
            usageLimit = true;
            lastJobData = err.response?.data?.job || null;
          } else {
            throw err;
          }
        }
      } else {
        try {
          hrResp = await axios.post(
            apiUrl("/api/hr-screening/generate"),
            (() => {
              const formData = new FormData();
              formData.append('job_url', jobUrl);
              formData.append('cv_file', cvFile);
              return formData;
            })(),
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              },
              withCredentials: true
            }
          );
          sessionId = hrResp?.data?.sessionId;
        } catch (err) {
          if (err.response?.status === 403 && err.response?.data?.usageLimit) {
            usageLimit = true;
            lastJobData = err.response?.data?.job || null;
          } else {
            throw err;
          }
        }
      }
      setJobUrl("");
      setFocusArea("");
      setDifficultyValue(0);
      setIsTechnical(false);
      setCvFile(null);
      if (usageLimit) {
        setUsageLimitReached(true);
        setLastJob(lastJobData);
        return;
      }
      if (isTechnical) {
        const job = resp?.data?.job;
        const image = resp?.data?.image;
        if (job?.status === 'failed') {
          alert('Job generation failed. Please try again.');
          return;
        }
        const jobId = job?.id;
        if (jobId) {
          // Store image data in sessionStorage for immediate display
          if (image) {
            sessionStorage.setItem('pendingImage', JSON.stringify({
              ...image,
              job_id: jobId
            }));
          }
          navigate(`/questions?job_id=${jobId}`);
        } else {
          console.warn('Job ID missing in response; navigating to generic image page.');
          navigate("/questions");
        }
      } else {
        if (sessionId) {
          navigate(`/hr-questions?session_id=${sessionId}`);
        } else {
          console.warn('Session ID missing in HR response');
          alert('Failed to generate HR questions. Please try again.');
        }
      }
    } catch (error) {
      console.error('Job submission failed:', error);
      alert('Submission failed. Check console for details.');
    } finally {
      setIsLoading(false);
      setCurrentStep(0);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar activeItem="new-question" />
      <div className="main-content">
        <div className="content-header">
          <h2>Create New Question Set</h2>
          <p>Paste a job post URL to generate tailored interview questions.</p>
        </div>

        {/* Interview Type Toggle */}
        <div className="interview-toggle">
          <span className={!isTechnical ? 'active' : ''}>HR Screening</span>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={isTechnical}
              onChange={() => setIsTechnical(!isTechnical)}
            />
            <span className="slider"></span>
          </label>
          <span className={isTechnical ? 'active' : ''}>Technical Interview</span>
        </div>

        <div className={`form-container${isLoading ? ' loading-mode' : ''}`}>
          {usageLimitReached && lastJob ? (
            <div className="last-job-container">
              <h3>Your Last Generated Question Set</h3>
              {lastJob.image_url && (
                <div className="last-job-image">
                  <img src={lastJob.image_url} alt="Generated" style={{ maxWidth: '100%', borderRadius: '12px', marginBottom: '1rem' }} />
                </div>
              )}
              {lastJob.questions && Array.isArray(lastJob.questions) && (
                <div className="last-job-questions">
                  <h4>Questions:</h4>
                  <ul>
                    {lastJob.questions.map((q, idx) => (
                      <li key={idx}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Add more fields as needed */}
            </div>
          ) : isLoading ? (
            <div className="loading-container">
              <div className="loading-progress">
                <div className="loading-spinner"></div>
                <div className="loading-steps">
                  {loadingSteps.map((step, index) => (
                    <div
                      key={index}
                      className={`loading-step ${index <= currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                    >
                      <div className="step-indicator">
                        {index < currentStep ? (
                          <span className="checkmarks">✓</span>
                        ) : (
                          <span className="step-number">{index + 1}</span>
                        )}
                      </div>
                      <span className="step-text">{step.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Job Post URL:</label>
                <input
                  type="url"
                  value={jobUrl}
                  onChange={(e) => setJobUrl(e.target.value)}
                  placeholder="https://example.com/job"
                  required
                />
              </div>
              {isTechnical ? (
                <>
                  <div className="form-group">
                    <label>Choose an Area of Interest and Focus:</label>
                    <select
                      value={focusArea}
                      onChange={(e) => setFocusArea(e.target.value)}
                      required
                    >
                      <option value="">Select a focus area</option>
                      {FOCUS_AREAS.map((area) => (
                        <option key={area} value={area}>
                          {area}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Career Level: <b id="bold"> {getDifficultyLabel(difficultyValue)} </b></label>
                    <div className="slider-container">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={difficultyValue}
                        onChange={(e) => setDifficultyValue(Number(e.target.value))}
                        className="difficulty-slider"
                      />
                      <div
                        className="slider-label"
                        style={{
                          left: `calc(${difficultyValue}% - 40px)`
                        }}
                      >
                        {getDifficultyLabel(difficultyValue)}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label>Upload Your CV/Resume:</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setCvFile(e.target.files[0])}
                    required
                  />
                </div>
              )}
              <button type="submit" className="submits-btn">Generate My Questions</button>
            </form>
          )}
        </div>
        <Feedback />
      </div>
      <div className="dashboard-footer">
        <Footer />
      </div>
    </div>
  );
}

export default Dashboard;